/**
 * Buying a plan on the web: the catalogue read, the two redirects, and the wait after one.
 *
 * It decides nothing. `model/webOffers` turns the catalogue into cards, `model/webCheckout` turns a
 * refusal into a sentence and an outcome into the next phase of the wait; this file owns the
 * queries, the clock, the browser's storage and the address.
 *
 * ── Three requests that all end by leaving the page ─────────────────────────────────────────
 * The payment page and the billing portal are the provider's, hosted, and reached by assigning
 * `window.location` to a URL the backend minted for this account (requirement R7). None of the
 * three is a link with an `href`: the address is created per press and is valid for minutes, so an
 * anchor would either be stale or would need a request to fill it in, which is this press.
 *
 * ── The picker's own reserve, and why it is not drawn on a cold load ────────────────────────
 * Which of the two faces this tab wears — the picker or a subscriber's rows — is the ENTITLEMENT's
 * answer, so there is nothing to reserve until that read has landed: a three-card skeleton drawn
 * before it would collapse for every trainer who turns out to be paying already, which is the jump
 * the policy forbids pointed the other way. Once the entitlement is known (every navigation inside
 * the app, every refetch, every retry), the block is present and the catalogue's own wait is drawn
 * inside its reserve with the skeleton gate over it, exactly as every other read on this screen.
 *
 * ── The session id outlives the address, and only just ──────────────────────────────────────
 * The two checkout parameters come off the URL as soon as they have been read, because a return
 * address reloaded is a completion call repeated. The id goes into `sessionStorage` for the length
 * of the wait instead, wrapped both ways: reading the property throws outright in a browser set to
 * block site data, and a wait that cannot be remembered still completes — it just cannot survive a
 * reload.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '~/shared/api/mutator';
import {
  postMeSubscriptionWebCheckout,
  postMeSubscriptionWebCheckoutReturn,
  postMeSubscriptionWebPortal,
} from '~/shared/api/generated/endpoints/web-checkout/web-checkout';
import {
  TrainWithMeDataEnumsBillingPeriod as BillingPeriod,
  TrainWithMeModulesSubscriptionsProcessorsWebCheckoutOutcome as Outcome,
  type TrainWithMeDataEnumsBillingPeriod as BillingPeriodValue,
} from '~/shared/api/generated/model';

import { entitlementsQueryKey, entitlementsQueryOptions, planCatalogQueryOptions } from '../api/accountQueries';
import { accountTabSearch } from '../model/accountTabs';
import {
  carriesTrialCode,
  trialCodeAtPress,
  trialCodeCopy,
  trialCodeFace,
  type TrialCodeCopy,
  type TrialCodeFace,
} from '../model/trialCode';
import {
  armCheckoutReturn,
  checkoutRefusal,
  checkoutReturnStep,
  isLeavableCheckoutUrl,
  RETURN_POLL_INTERVAL_MS,
  type CheckoutProblem,
  type CheckoutReturnOutcome,
  type CheckoutReturnPhase,
} from '../model/webCheckout';
import { storeTargetOf, storeUrlFor } from '../model/subscriptionCard';
import {
  planChangeRoute,
  planFace,
  webOffers,
  type PlanChangeRoute,
  type PlanFace,
  type PlanOffer,
} from '../model/webOffers';
import { useSectionRead, type SectionState } from './useSectionRead';
import { useTrialCode, type TrialCodeApi } from './useTrialCode';

/** Where the provider's checkout session id waits out a reload. */
const SESSION_KEY = 'movebuddy.checkout.session';

/* `sessionStorage` is read through the property, which THROWS before any method is called in a
 * browser with site data blocked — the same trap `shared/lib/preferenceStore` records for
 * `localStorage`. Every one of the three touches is wrapped, and a store that is not there simply
 * means the wait cannot survive a reload. */
function readSession(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function writeSession(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(SESSION_KEY);
    else window.sessionStorage.setItem(SESSION_KEY, value);
  } catch {
    /* Nothing to do and nothing to report: the wait still completes in this document. */
  }
}

/** As much of an `ApiError`'s problem document as `model/webCheckout` reads. */
function problemOf(error: unknown): CheckoutProblem {
  if (!(error instanceof ApiError) || error.problem === undefined) return {};
  const problem: Record<string, unknown> = { ...error.problem };
  const text = (key: string): string | undefined =>
    typeof problem[key] === 'string' ? problem[key] : undefined;
  return { code: error.code, channel: text('channel'), compPlanCode: text('compPlanCode') };
}

interface ReturnFlow {
  readonly phase: CheckoutReturnPhase;
  /** Sent with the FIRST call only; every poll after it asks without one. */
  readonly sessionId: string | null;
  /** When the wait began, so the minute's cap is measured from one point rather than per poll. */
  readonly startedAt: number;
  /** Bumped per call. It is what the calling effect keys on, and its own re-entry guard. */
  readonly attempt: number;
}

const IDLE: ReturnFlow = { phase: 'idle', sessionId: null, startedAt: 0, attempt: 0 };

/**
 * Which of the two faces the block under the seats card wears — and the third value is not a
 * detail, it is the whole reserve.
 *
 * `pending` is „the entitlement has not answered, so nobody knows yet". The tab draws the PICKER's
 * reserve for it, because the picker is the taller of the two and whatever stands under this block
 * must not be lifted when the answer lands. Reading `isFree` off a read that has not answered says
 * `false`, which drew a subscriber's „Odnowienie —" rows at every free trainer on a cold load and
 * unmounted them a moment later.
 */
export type CheckoutFace = 'pending' | 'picker' | 'rows';

export interface WebCheckoutApi {
  /** Which face this tab wears: the picker, the subscriber's rows, or neither yet. */
  readonly face: CheckoutFace;
  readonly state: SectionState;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly onRetry: () => void;
  readonly offers: readonly PlanOffer[];
  /**
   * Where a card's press leads, or `null` while the entitlement has not answered. The cards stand
   * for every trainer; the channel decides what they do (`model/webOffers`' `planChangeRoute`).
   */
  readonly route: PlanChangeRoute | null;
  /** The store that manages this subscription, on the `store` route only. */
  readonly store: { readonly url: string; readonly name: string } | null;
  /** The period a card is showing, and the face that goes with it. */
  readonly faceOf: (offer: PlanOffer) => PlanFace | null;
  readonly periodOf: (offer: PlanOffer) => BillingPeriodValue | null;
  readonly onSelectPeriod: (code: string, period: BillingPeriodValue) => void;
  /** The code row's state and its acts (R19). One card carries it — see `trialFaceOf`. */
  readonly trial: TrialCodeApi;
  /** The code row's face on this card, or `null` for a card that carries no code row. */
  readonly trialFaceOf: (offer: PlanOffer) => TrialCodeFace | null;
  /** What the code on the card changes in this card's words, or `null`. */
  readonly trialCopyOf: (offer: PlanOffer) => TrialCodeCopy | null;
  /** The sentence the screen's live region owes the trainer about the code. Empty for none. */
  readonly trialAnnouncement: string;
  /**
   * „Przełącz na rocznie", beside a code standing on the month. The press that removes this button
   * is also what draws „Usuń", so focus moves there rather than falling to the document.
   */
  readonly onTrialToAnnual: (offer: PlanOffer) => void;
  /** Whether anything on this block can actually be bought. False with the provider unreachable. */
  readonly sellable: boolean;
  /** The plan code whose button is busy, or `null`. Every other button refuses while one is. */
  readonly buying: string | null;
  readonly onBuy: (offer: PlanOffer) => void;
  /** The refusal under the cards, already worded. */
  readonly refusal: string | null;
  /** „Aktywujemy…" or the sentence that replaces it when the wait ends, one way or the other. */
  readonly notice: string | null;
  /** Whether the notice is one a trainer can do something about — the minute having run out. */
  readonly onRetryActivation: (() => void) | null;
  readonly portalBusy: boolean;
  readonly portalError: string | null;
  readonly onOpenPortal: () => void;
}

/**
 * @param active whether Subskrypcja is the tab on screen.
 */
export function useWebCheckout(active: boolean): WebCheckoutApi {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_shell/konto' });

  const entitlements = useQuery({ ...entitlementsQueryOptions(), enabled: active });
  /* One cache entry, shared with `useSubscriptionCard` — the same `queryOptions`, so the tab makes
   * ONE entitlement request whichever hook asks first. */
  const isFree = entitlements.data?.isFree === true;
  /* An ERROR takes the rows, not the reserve: the tab already draws a banner and a retry above
   * them, and a block that waits for ever under a failure that has been reported is a second,
   * silent failure. Everything else waits for the answer — see `CheckoutFace`. */
  const face: CheckoutFace = entitlements.isError
    ? 'rows'
    : entitlements.data === undefined
      ? 'pending'
      : isFree
        ? 'picker'
        : 'rows';
  /* The catalogue is read for EVERY trainer. It used to be read for a free one only, and a trainer
   * who already paid was shown no other level at all — nothing on the tab said the plan could be
   * changed, or where (owner, 23 September 2026). */
  const catalog = useQuery({ ...planCatalogQueryOptions(), enabled: active });
  const route = entitlements.data === undefined ? null : planChangeRoute(entitlements.data);
  const storeTarget = entitlements.data === undefined ? null : storeTargetOf(entitlements.data);

  const [periods, setPeriods] = useState<Readonly<Record<string, BillingPeriodValue>>>({});
  const [buying, setBuying] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  /* ── The page comes back from the provider with its last state still on it ────────────────
   * Leaving for Stripe never unmounts this document: the browser parks it in the back-forward
   * cache with `buying` or `portalBusy` still set from the press that left, and the Back button
   * restores it that way — a button wearing `aria-disabled` that answers nothing (staging,
   * 22 September 2026). `pageshow` with `persisted` is the one signal of that restore. */
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setBuying(null);
      setPortalBusy(false);
      // Back from the portal is the moment a plan may have changed, and a restored page fires no
      // focus event for `refetchOnWindowFocus` to hear. The entitlement is asked again here.
      void queryClient.invalidateQueries({ queryKey: entitlementsQueryKey });
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [queryClient]);
  /* ── Arming, in the FIRST render rather than in an effect ──────────────────────────────────
   * The address and the stored id say everything they are going to say before anything is drawn,
   * so the wait starts as initial state. An effect that set it instead would render the picker for
   * one frame and replace it with the activating notice in the next — a jump, and the lint rule
   * against a `setState` in an effect body names the same cost. */
  /* The link's trial code, read on the same first render as the return parameters and for the same
   * reason: it fills the card before anything is drawn. */
  const [linkedCode] = useState(() => search.code ?? null);
  const trial = useTrialCode(linkedCode);
  const [arming] = useState(() =>
    armCheckoutReturn({
      checkout: search.checkout ?? null,
      sessionId: search.session_id ?? null,
      stored: readSession(),
    }),
  );
  const [flow, setFlow] = useState<ReturnFlow>(() =>
    arming.phase === 'idle'
      ? IDLE
      : { phase: arming.phase, sessionId: arming.sessionId, startedAt: Date.now(), attempt: 0 },
  );
  /* The arming's SIDE effects — the browser's storage and the address — which are an effect's work
   * and not a render's. Once per mount: the arming itself never changes. */
  const armedOnce = useRef(false);
  useEffect(() => {
    if (armedOnce.current) return;
    armedOnce.current = true;

    writeSession(arming.remember);
    // The code is on the card now; a link carrying one is not an address to stay at either.
    if (arming.clearAddress || linkedCode !== null) {
      // REPLACES: the return address is not a step in the trainer's history, and Back landing on it
      // would re-run the completion.
      void navigate({ to: '.', search: () => accountTabSearch('subskrypcja'), replace: true });
    }
  }, [arming, linkedCode, navigate]);

  /* ── One call per attempt, and the poll that schedules the next ───────────────────────────────
   * There is no cleanup here, and that is deliberate. An abort flag flipped by the cleanup meant
   * StrictMode's double-invoke ran the call, tore the effect down, and then threw the answer away
   * while the guard below stopped the second invocation from calling again — so the completion was
   * made and never applied, and the tab sat on „Aktywujemy…" for ever. The ref is the whole guard:
   * it survives the remount, so exactly one call is made per attempt and its answer always lands.
   * A `setState` after unmount is a no-op in React 19. */
  const ran = useRef(-1);
  useEffect(() => {
    if (flow.phase !== 'completing' && flow.phase !== 'activating') return;
    if (ran.current === flow.attempt) return;
    ran.current = flow.attempt;

    const call = async () => {
      // The id goes with the first call only. A poll asks the server to look at the account's
      // subscriptions instead, which is also what heals a trainer who never came back at all.
      const first = flow.attempt === 0 && flow.sessionId !== null;
      let outcome: CheckoutReturnOutcome;
      try {
        const answer = await postMeSubscriptionWebCheckoutReturn(
          first ? { checkoutSessionId: flow.sessionId } : {},
        );
        outcome = answer.outcome ?? 'failed';
        if (outcome === Outcome.Entitled && answer.entitlements !== null && answer.entitlements !== undefined) {
          // Primed rather than invalidated: the answer already carries the entitlement in the shape
          // `GET /me/entitlements` returns, so a second round trip would only re-ask for it.
          //
          // CANCELLED first, which is not a formality: the tab's own entitlement read is opened on
          // the same mount as this call, and whichever lands second wins. Without this the fresh
          // subscription was written into the cache and then overwritten by the read that had been
          // in flight since before the purchase, so the tab went back to offering plans to somebody
          // who had just bought one. It is TanStack's own documented order for a primed write.
          await queryClient.cancelQueries({ queryKey: entitlementsQueryKey });
          queryClient.setQueryData(entitlementsQueryKey, answer.entitlements);
        } else if (outcome === Outcome.Entitled) {
          // Entitled, and the answer did not carry the entitlement to prime with. Settling on that
          // alone would leave the picker standing in front of somebody who has just paid, so the
          // cached read is invalidated and the tab takes its face from the refetch instead.
          void queryClient.invalidateQueries({ queryKey: entitlementsQueryKey });
        }
      } catch {
        outcome = 'failed';
      }

      const step = checkoutReturnStep({ outcome, waitedMs: Date.now() - flow.startedAt });
      if (step.forget) writeSession(null);
      setFlow((current) => ({ ...current, phase: step.phase }));
    };

    void call();
  }, [flow, queryClient]);

  /* The poll's timer lives in its OWN effect, and that is not a style choice: scheduling it inside
   * the effect above meant the state change that ended one attempt tore that effect down and
   * cleared the timer it had just set, so the wait polled exactly once. Here the timer belongs to
   * the phase it is waiting in, and is armed only for an attempt that has already answered. */
  useEffect(() => {
    if (flow.phase !== 'activating' || ran.current !== flow.attempt) return;
    const timer = setTimeout(() => {
      setFlow((current) => ({ ...current, attempt: current.attempt + 1 }));
    }, RETURN_POLL_INTERVAL_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [flow.phase, flow.attempt]);

  /* ── The picker ───────────────────────────────────────────────────────────────────────────── */

  /* The picker's own wait covers BOTH reads: the entitlement decides what the cards' buttons do and
   * which one is current, and the catalogue decides what is on them. */
  const section = useSectionRead({
    active,
    reads: [entitlements, catalog],
  });

  const offers =
    entitlements.data === undefined || catalog.data === undefined
      ? []
      : webOffers({
          plans: catalog.data,
          currentPlanCode: entitlements.data?.plan?.code ?? null,
          offerFree: route === 'portal' || route === 'store',
          t,
        });

  /* The catalogue is the only thing that can turn `pro` into „Pro", and this hook is holding it.
   * A code the catalogue does not carry resolves to `null`, and the sentence loses the name. */
  const planNameOf = (code: string): string | null => {
    const named = catalog.data?.find((plan) => plan.code === code);
    const name = named?.name;
    return typeof name === 'string' && name !== '' ? name : null;
  };

  const periodOf = useCallback(
    (offer: PlanOffer) => periods[offer.code] ?? offer.defaultPeriod,
    [periods],
  );

  /* The provider's page, and the end of this document's life. An answer with no URL in it — or with
   * one this app will not navigate to (`model/webCheckout`'s rule) — is a failure like any other and
   * falls into the same refusal sentence rather than into `window.location`. */
  const leaveFor = (url: string | null | undefined) => {
    if (!isLeavableCheckoutUrl(url)) throw new Error('The checkout answer carried no usable URL.');
    window.location.assign(url);
  };

  const trialFaceOf = (offer: PlanOffer): TrialCodeFace | null =>
    carriesTrialCode(offer)
      ? trialCodeFace({ code: trial.applied, period: periodOf(offer), route })
      : null;

  const trialCopyOf = (offer: PlanOffer): TrialCodeCopy | null => {
    const face = trialFaceOf(offer);
    return face === null ? null : trialCodeCopy(face, offer, t);
  };

  const onBuy = (offer: PlanOffer) => {
    const period = periodOf(offer);
    if (period === null || buying !== null) return;
    // A code still standing in the open field is applied by this press rather than dropped.
    const press = carriesTrialCode(offer)
      ? trialCodeAtPress({
          code: trial.applied,
          draft: trial.draft,
          editing: trial.editing,
          period,
          route,
        })
      : null;
    if (press !== null && press.applies !== null) trial.onAppliedByPress(press.applies);
    // ABSENT rather than null when there is none: a checkout without a code is asked for exactly as
    // it was before R19, byte for byte.
    const trialCode = press?.checkoutCode ?? null;
    setBuying(offer.code);
    setRefusal(null);
    postMeSubscriptionWebCheckout({
      planCode: offer.code,
      billingPeriod: period,
      ...(trialCode === null ? {} : { trialCode }),
    })
      .then((answer) => {
        leaveFor(answer.url);
      })
      .catch((error: unknown) => {
        setBuying(null);
        const refused = checkoutRefusal(problemOf(error), t, planNameOf);
        if (refused.target === 'code' && trialCode !== null) trial.onRefused(trialCode, refused.message);
        else setRefusal(refused.message);
      });
  };

  const onOpenPortal = () => {
    if (portalBusy) return;
    setPortalBusy(true);
    setPortalError(null);
    postMeSubscriptionWebPortal()
      .then((answer) => {
        leaveFor(answer.url);
      })
      .catch(() => {
        setPortalBusy(false);
        setPortalError(t('web:account.subscription.portalFailed'));
      });
  };

  /* One press, and the same minute over again: the cap is measured from `startedAt`, so a retry
   * that reset only the attempt would answer once and give up. `sessionId` is deliberately dropped
   * — the id was spent on the first call, and a poll asks the server to look at the account. */
  const onRetryActivation = () => {
    setFlow((current) => ({
      phase: 'completing',
      sessionId: null,
      startedAt: Date.now(),
      attempt: current.attempt + 1,
    }));
  };

  return {
    face,
    ...section,
    offers,
    route,
    store:
      route === 'store' && storeTarget !== null
        ? {
            url: storeUrlFor(storeTarget),
            name: t(`web:account.subscription.store.${storeTarget}`),
          }
        : null,
    faceOf: (offer) => planFace(offer, periodOf(offer)),
    periodOf,
    onSelectPeriod: (code, period) => {
      setPeriods((current) => ({ ...current, [code]: period }));
    },
    trial,
    trialFaceOf,
    trialCopyOf,
    trialAnnouncement:
      offers.map((offer) => trialCopyOf(offer)?.announcement ?? null).find((said) => said !== null) ??
      '',
    onTrialToAnnual: (offer) => {
      flushSync(() => {
        setPeriods((current) => ({ ...current, [offer.code]: BillingPeriod.Annual }));
      });
      trial.removeRef.current?.focus();
    },
    buying,
    onBuy,
    refusal,
    sellable: offers.some((offer) => offer.periods.length > 0),
    notice:
      flow.phase === 'completing' || flow.phase === 'activating'
        ? t('web:account.subscription.activating')
        : flow.phase === 'stillActivating'
          ? t('web:account.subscription.activatingSlow')
          : flow.phase === 'nothingPurchased'
            ? t('web:account.subscription.notPurchased')
            : null,
    onRetryActivation: flow.phase === 'stillActivating' ? onRetryActivation : null,
    portalBusy,
    portalError,
    onOpenPortal,
  };
}
